import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Camera, CheckCircle2, LifeBuoy, Send, Trash2, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  IssueReport, createReport, finalizeReport, getReport, uploadScreenshot,
} from '../api/reportIssueApi';

/**
 * Global "Report an issue" drawer, available to every authenticated user.
 *
 * Open it from anywhere with:
 *   window.dispatchEvent(new CustomEvent('nexus:report-issue'))
 * (wired to the Cmd+K palette and the nav rail help button).
 *
 * The draft (title/description) persists in localStorage, and after
 * submitting, the report id is remembered for 30 minutes — so the user can
 * close the drawer, go take screenshots, and come back to attach them. The
 * triage agent waits a grace period (or until "Send for triage") before
 * reading the report.
 */

const DRAFT_KEY = 'nexus_issue_draft';
const ACTIVE_KEY = 'nexus_issue_active_report';
const ACTIVE_TTL_MS = 30 * 60 * 1000;

interface ActiveRef { id: string; savedAt: number }

function loadDraft(): { title: string; body: string } {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* corrupt draft — start fresh */ }
  return { title: '', body: '' };
}

function loadActive(): ActiveRef | null {
  try {
    const raw = localStorage.getItem(ACTIVE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ActiveRef;
    if (Date.now() - parsed.savedAt > ACTIVE_TTL_MS) {
      localStorage.removeItem(ACTIVE_KEY);
      return null;
    }
    return parsed;
  } catch { return null; }
}

export const ReportIssueModal: React.FC = () => {
  const { t } = useTranslation(['reportIssue', 'common']);
  const [isOpen, setIsOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [report, setReport] = useState<IssueReport | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [finalized, setFinalized] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const openModal = useCallback(async () => {
    setError(null);
    const draft = loadDraft();
    setTitle(draft.title);
    setBody(draft.body);
    const active = loadActive();
    if (active) {
      try {
        const r = await getReport(active.id);
        if (r.status === 'new') {
          setReport(r);
          setFinalized(false);
        } else {
          setReport(r);
          setFinalized(true);
        }
      } catch {
        localStorage.removeItem(ACTIVE_KEY);
        setReport(null);
      }
    } else {
      setReport(null);
      setFinalized(false);
    }
    setIsOpen(true);
  }, []);

  useEffect(() => {
    const handler = () => { void openModal(); };
    window.addEventListener('nexus:report-issue', handler);
    return () => window.removeEventListener('nexus:report-issue', handler);
  }, [openModal]);

  useEffect(() => {
    if (!isOpen) return;
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setIsOpen(false); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [isOpen]);

  const saveDraft = (tVal: string, bVal: string) => {
    try { localStorage.setItem(DRAFT_KEY, JSON.stringify({ title: tVal, body: bVal })); } catch { /* full */ }
  };

  const submit = async () => {
    if (title.trim().length < 3) { setError(t('reportIssue.addShortTitle', 'Ingresa primero un título breve')); return; }
    setSubmitting(true);
    setError(null);
    try {
      const created = await createReport(title.trim(), body.trim());
      localStorage.setItem(ACTIVE_KEY, JSON.stringify({ id: created.id, savedAt: Date.now() } satisfies ActiveRef));
      localStorage.removeItem(DRAFT_KEY);
      setReport(await getReport(created.id));
      setFinalized(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('reportIssue.submitFailed', 'No se pudo enviar el reporte'));
    } finally {
      setSubmitting(false);
    }
  };

  const addFiles = async (files: FileList | File[]) => {
    if (!report) return;
    setUploading(true);
    setError(null);
    try {
      for (const file of Array.from(files)) {
        if (!file.type.startsWith('image/')) continue;
        await uploadScreenshot(report.id, file);
      }
      setReport(await getReport(report.id));
    } catch (e) {
      setError(e instanceof Error ? e.message : t('reportIssue.uploadFailed', 'No se pudieron subir las capturas'));
    } finally {
      setUploading(false);
    }
  };

  const finalize = async () => {
    if (!report) return;
    try {
      await finalizeReport(report.id);
      setFinalized(true);
      localStorage.removeItem(ACTIVE_KEY);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('reportIssue.triageFailed', 'No se pudo enviar a triaje'));
    }
  };

  const startNew = () => {
    localStorage.removeItem(ACTIVE_KEY);
    setReport(null);
    setFinalized(false);
    setTitle('');
    setBody('');
  };

  if (!isOpen) return null;

  const inputStyle: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box', padding: '8px 10px',
    border: '1px solid var(--color-border)', borderRadius: 4, fontSize: 13,
    color: 'var(--color-text)', backgroundColor: 'var(--color-base)', outline: 'none',
  };
  const btnStyle = (primary = false): React.CSSProperties => ({
    height: 32, padding: '0 14px', borderRadius: 4, fontSize: 13, fontWeight: 500,
    display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer',
    border: `1px solid ${primary ? '#7C3AED' : 'var(--color-border)'}`,
    backgroundColor: primary ? '#7C3AED' : 'transparent',
    color: primary ? '#FFFFFF' : 'var(--color-text)',
  });

  return (
    <div
      onClick={() => setIsOpen(false)}
      onPaste={(e) => {
        const images = Array.from(e.clipboardData?.files || []).filter((f) => f.type.startsWith('image/'));
        if (images.length && report && !finalized) void addFiles(images);
      }}
      style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.35)', zIndex: 9998, display: 'flex', justifyContent: 'flex-end' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 480, maxWidth: '94vw', height: '100%',
          backgroundColor: 'var(--color-surface)', borderLeft: '1px solid var(--color-border)',
          display: 'flex', flexDirection: 'column', zIndex: 9999,
        }}
      >
        {/* Header */}
        <div style={{ height: 52, padding: '0 16px', borderBottom: '1px solid var(--color-border)', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          <LifeBuoy size={16} color="#7C3AED" />
          <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-text)' }}>{t('reportIssue.title', 'Reportar un problema')}</span>
          <button onClick={() => setIsOpen(false)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', display: 'flex' }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {report === null ? (
            <>
              <div style={{ fontSize: 12.5, color: 'var(--color-text-muted)', lineHeight: 1.55 }}>
                {t('reportIssue.intro', '¿Algo no funciona o se comporta de forma extraña? Descríbelo y el equipo de la plataforma lo recibirá junto con tus capturas de pantalla y los errores que registraron tu navegador y los servidores en este momento. Tu borrador se guarda automáticamente: puedes cerrar esta ventana, tomar capturas y volver.')}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text)' }}>{t('reportIssue.whatHappened', '¿Qué sucedió?')}</label>
                <input
                  style={inputStyle}
                  value={title}
                  autoFocus
                  maxLength={200}
                  placeholder={t('reportIssue.whatHappenedPlaceholder', "Resumen breve, ej. 'Exportar a Excel no hace nada'")}
                  onChange={(e) => { setTitle(e.target.value); saveDraft(e.target.value, body); }}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text)' }}>{t('reportIssue.details', 'Detalles')}</label>
                <textarea
                  style={{ ...inputStyle, height: 200, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }}
                  value={body}
                  placeholder={t('reportIssue.detailsPlaceholder', '¿Qué estabas haciendo, qué esperabas que pasara y qué ocurrió?')}
                  onChange={(e) => { setBody(e.target.value); saveDraft(title, e.target.value); }}
                />
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--color-text-subtle)' }}>
                {t('reportIssue.screenshotNotice', 'Las capturas de pantalla se adjuntan en el siguiente paso, después de enviar.')}
              </div>
            </>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <CheckCircle2 size={16} color="#22C55E" style={{ marginTop: 2, flexShrink: 0 }} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text)' }}>{report.title}</div>
                  <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 2 }}>
                    {finalized || report.status !== 'new'
                      ? t('reportIssue.submittedNotice', 'Reporte enviado. El equipo ha sido notificado.')
                      : t('reportIssue.createdNotice', 'Reporte creado. Agrega capturas de pantalla ahora o cierra esta ventana, tómalas y regresa luego — este reporte permanecerá abierto para ti por 30 minutos.')}
                  </div>
                </div>
              </div>

              {report.triage_summary && (
                <div style={{ fontSize: 12.5, color: 'var(--color-text)', lineHeight: 1.5, backgroundColor: 'var(--color-base)', border: '1px solid var(--color-border)', borderRadius: 4, padding: 10 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>
                    {t('reportIssue.triageResult', 'Resultado del triaje')}
                  </div>
                  {report.triage_summary}
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text)' }}>
                  {t('reportIssue.screenshotsCount', 'Capturas de pantalla ({{count}}/10)', { count: report.attachments.length })}
                </div>
                {report.attachments.map((a) => (
                  <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--color-text-muted)' }}>
                    <Camera size={13} />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.filename}</span>
                    <span style={{ marginLeft: 'auto', flexShrink: 0 }}>{(a.size_bytes / 1024).toFixed(0)} KB</span>
                  </div>
                ))}
                {!finalized && report.status === 'new' && (
                  <>
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/*"
                      multiple
                      style={{ display: 'none' }}
                      onChange={(e) => { if (e.target.files?.length) void addFiles(e.target.files); e.target.value = ''; }}
                    />
                    <button style={btnStyle()} disabled={uploading} onClick={() => fileRef.current?.click()}>
                      <Camera size={14} /> {uploading ? t('reportIssue.uploading', 'Subiendo…') : t('reportIssue.addScreenshots', 'Agregar capturas de pantalla')}
                    </button>
                    <div style={{ fontSize: 11.5, color: 'var(--color-text-subtle)' }}>
                      {t('reportIssue.pasteTip', 'Consejo: también puedes pegar una imagen aquí (Ctrl+V) tras capturar la pantalla.')}
                    </div>
                  </>
                )}
              </div>
            </>
          )}
          {error && <div style={{ fontSize: 12, color: '#EF4444' }}>{error}</div>}
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 18px', borderTop: '1px solid var(--color-border)', display: 'flex', gap: 8, justifyContent: 'flex-end', flexShrink: 0 }}>
          {report === null ? (
            <>
              <button style={btnStyle()} onClick={() => setIsOpen(false)}>{t('reportIssue.closeDraftSaved', 'Cerrar (borrador guardado)')}</button>
              <button style={{ ...btnStyle(true), opacity: submitting ? 0.6 : 1 }} disabled={submitting} onClick={() => void submit()}>
                <Send size={14} /> {submitting ? t('reportIssue.submitting', 'Enviando…') : t('reportIssue.submitReport', 'Enviar reporte')}
              </button>
            </>
          ) : finalized || report.status !== 'new' ? (
            <>
              <button style={btnStyle()} onClick={startNew}><Trash2 size={13} /> {t('reportIssue.newReport', 'Nuevo reporte')}</button>
              <button style={btnStyle(true)} onClick={() => setIsOpen(false)}>{t('common.done', 'Listo')}</button>
            </>
          ) : (
            <>
              <button style={btnStyle()} onClick={() => setIsOpen(false)}>{t('reportIssue.closeAddLater', 'Cerrar, agregaré capturas luego')}</button>
              <button style={btnStyle(true)} onClick={() => void finalize()}>
                <Send size={14} /> {t('reportIssue.sendForTriage', 'Enviar a triaje ahora')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportIssueModal;
